import axios from "axios";
import { toast } from "sonner";
import { Trash2, FileText, Gavel, Lock, Unlock, Check, X, BookOpen } from "lucide-react";
import { API } from "../../context/AuthContext";
import { TRACK_ACCENT, eventShape } from "./skins";

const PARTY_BADGE = {
  petitioner: "text-sky-400 bg-sky-500/10 border-sky-500/30",
  respondent: "text-orange-400 bg-orange-500/10 border-orange-500/30",
};

const REVIEW_BADGE = {
  needs_review: { label: "Needs Review", cls: "text-amber-400 bg-amber-500/10 border-amber-500/40" },
  approved: { label: "Verified", cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/40" },
  rejected: { label: "Rejected", cls: "text-rose-400 bg-rose-500/10 border-rose-500/40" },
};

export function EventCard({ event, skin, onDelete, horizontal, publicView, onVault, publicSlug, onReview, onSource }) {
  const review = !publicView && REVIEW_BADGE[event.review_status];
  const shape = eventShape(event);
  const accent = TRACK_ACCENT[event.tracks[0]] || TRACK_ACCENT.procedural;
  const isPhoto = event.source_file_id && ["image", "photo"].includes(event.source);
  const photoUrl = isPhoto
    ? publicView
      ? publicSlug ? `${API}/public/${publicSlug}/files/${event.source_file_id}/image` : null
      : `${API}/files/${event.source_file_id}/image`
    : null;

  const openDoc = async () => {
    try {
      const res = await axios.get(`${API}/files/${event.source_file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch {
      toast.error("Couldn't open the source document.");
    }
  };

  return (
    <div
      className={`relative border border-l-4 ${accent} p-4 rounded-xl space-y-2 transition-colors duration-200 group ${skin.card} ${horizontal ? "h-full" : ""}`}
      data-testid={`event-card-${event.id}`}
    >
      <div className="flex justify-between items-center gap-2 text-[10px] font-mono">
        <span className={`border px-2 py-0.5 rounded whitespace-nowrap ${skin.chip}`}>{event.label}</span>
        <div className="flex items-center gap-1 flex-wrap justify-end">
          {review && (
            <span className={`text-[9px] border px-1 rounded uppercase font-bold ${review.cls}`} data-testid={`review-badge-${event.id}`}>{review.label}</span>
          )}
          {event.vaulted && (
            <span className="flex items-center gap-0.5 text-[9px] bg-emerald-500/10 border border-emerald-500/40 px-1 rounded text-emerald-400 uppercase font-bold" data-testid={`vaulted-badge-${event.id}`}>
              <Lock className="w-2.5 h-2.5" /> Vaulted
            </span>
          )}
          {shape === "hearing" && (
            <span className="flex items-center gap-0.5 text-[9px] bg-[#f59e0b]/10 border border-[#f59e0b]/40 px-1 rounded text-[#f59e0b] uppercase font-bold" data-testid={`hearing-badge-${event.id}`}>
              <Gavel className="w-2.5 h-2.5" /> Hearing
            </span>
          )}
          {event.party && event.party !== "neutral" && (
            <span className={`text-[9px] border px-1 rounded uppercase font-bold ${PARTY_BADGE[event.party]}`} data-testid={`party-badge-${event.id}`}>{event.party}</span>
          )}
          {event.tracks.map((t) => (
            <span key={t} className="text-[9px] bg-black/20 px-1 rounded text-gray-400 uppercase font-bold">{t}</span>
          ))}
          {event.source && event.source !== "text" && (
            <span className="text-[9px] bg-[#2dd4bf]/10 border border-[#2dd4bf]/20 px-1 rounded text-[#2dd4bf] uppercase font-bold">{event.source}</span>
          )}
          {!!onVault && (
            <button
              onClick={() => onVault(event, !event.vaulted)}
              data-testid={`vault-event-btn-${event.id}`}
              title={event.vaulted ? "Release from vault" : "Hide in the PIN-locked vault"}
              className="ml-1 text-gray-400 hover:text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              {event.vaulted ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
            </button>
          )}
          {!!onDelete && (
            <button
              onClick={() => onDelete(event.id)}
              data-testid={`delete-event-btn-${event.id}`}
              className="ml-1 text-gray-400 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <h4 className={`font-bold text-sm ${skin.title}`}>{event.title}</h4>
      {photoUrl && (
        <img
          src={photoUrl}
          alt={event.title}
          loading="lazy"
          onClick={!publicView ? openDoc : undefined}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          data-testid={`event-photo-${event.id}`}
          className={`w-full max-h-52 object-cover rounded-lg border border-white/10 ${!publicView ? "cursor-pointer hover:opacity-90 transition-opacity duration-200" : ""}`}
        />
      )}
      <p className={`text-xs leading-relaxed ${skin.desc}`}>{event.description}</p>
      {!publicView && event.document_id && (event.source_page || event.original_date_text) && (
        <div className={`flex flex-wrap items-center gap-2 text-[10px] font-mono ${skin.desc}`}>
          {event.source_page && onSource && (
            <button onClick={() => onSource(event)} data-testid={`event-cite-btn-${event.id}`} title={event.citation ? `“${event.citation}”` : "Open the cited page"}
              className="flex items-center gap-1 font-bold text-[#2dd4bf] border border-[#2dd4bf]/30 rounded px-1.5 py-0.5 hover:bg-[#2dd4bf]/10 transition-colors duration-200">
              <BookOpen className="w-3 h-3" /> p.{event.source_page}
            </button>
          )}
          {event.original_date_text && event.date_role !== "upload" && <span className="truncate max-w-[260px]">as written: “{event.original_date_text}”</span>}
          {onReview && event.review_status === "needs_review" && (
            <span className="flex gap-1 ml-auto">
              <button onClick={() => onReview(event, "approve")} data-testid={`event-approve-btn-${event.id}`} title="Approve this event" className="p-1 rounded border text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10"><Check className="w-3 h-3" /></button>
              <button onClick={() => onReview(event, "reject")} data-testid={`event-reject-btn-${event.id}`} title="Reject this event" className="p-1 rounded border text-rose-400 border-rose-500/30 hover:bg-rose-500/10"><X className="w-3 h-3" /></button>
            </span>
          )}
        </div>
      )}
      {event.source_file_id && !publicView && (
        <button
          onClick={openDoc}
          data-testid={`open-doc-btn-${event.id}`}
          className={`flex items-center gap-1.5 text-[10px] font-mono border px-2 py-1 rounded-lg transition-colors duration-200 max-w-full ${skin.light ? "text-teal-800 border-teal-700/40 hover:bg-teal-700/10" : "text-[#2dd4bf] border-[#2dd4bf]/25 hover:border-[#2dd4bf]/60"}`}
          title="Open the source document"
        >
          <FileText className="w-3 h-3 shrink-0" />
          <span className="truncate">{event.source_name || "Source document"}</span>
        </button>
      )}
    </div>
  );
}

export function Marker({ event }) {
  const shape = eventShape(event);
  if (shape === "hearing")
    return <div className="w-3.5 h-3.5 rotate-45 rounded-[2px] bg-[#f59e0b] ring-2 ring-[#f59e0b]/40 shrink-0" data-testid="marker-hearing" />;
  if (shape === "filing")
    return <div className="w-3 h-3 rounded-[2px] bg-purple-500 ring-2 ring-purple-500/40 shrink-0" data-testid="marker-filing" />;
  if (shape === "incident")
    return <div className="w-3.5 h-3.5 rounded-full border-2 border-rose-500 ring-2 ring-rose-500/30 shrink-0" data-testid="marker-incident" />;
  const color = event.status === "pending" ? "bg-purple-400 ring-purple-400/40" : "bg-blue-500 ring-blue-500/40";
  return <div className={`w-3 h-3 rounded-full ring-2 shrink-0 ${color}`} data-testid="marker-default" />;
}
